/**
 * A numeric profile field the brand left empty arrives as null. Rows saved
 * before the upsert input accepted null still hold 0, which no brand can mean
 * as a real year or price, so both count as "no value" everywhere we render.
 */
export const hasBrandProfileValue = (value?: number | null): boolean =>
  typeof value === 'number' && !Number.isNaN(value) && value !== 0

/**
 * The value to render for a numeric profile field, or null when there is none.
 * A negative year or price is meaningless and the editor cannot produce one,
 * so a stored negative is shown by its magnitude rather than as `$-50`.
 */
export const toBrandProfileValue = (value?: number | null): number | null =>
  hasBrandProfileValue(value) ? Math.abs(value as number) : null

/**
 * Renders a price range from whichever ends the brand filled in. A half-filled
 * pair is still worth showing, so it reads as an open range rather than as
 * `$50 - $`, which would look like a missing number instead of a missing end.
 * Returns null when neither end has a value and the line should be hidden.
 */
export const formatBrandProfileRange = (
  min?: number | null,
  max?: number | null,
): string | null => {
  const minEnd = toBrandProfileValue(min)
  const maxEnd = toBrandProfileValue(max)
  if (minEnd !== null && maxEnd !== null) return `$${minEnd} - $${maxEnd}`
  if (minEnd !== null) return `From $${minEnd}`
  if (maxEnd !== null) return `Up to $${maxEnd}`
  return null
}
